import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '@/api/queries/auth';
import { useOrganizationQuery } from '@/api/queries/organizations';
import type { Organization } from '@/api/models';
import { PATH } from '@/router/PATH';

/** Shown in place of a missing value, never a raw i18n key. */
export const ONBOARDING_FALLBACK = '—';

export type UseOnboardingSessionResult = {
    /** The organisation once read (the gate has, so this is a cache hit). */
    organisation: Organization | undefined;
    organisationName: string;
    isSigningOut: boolean;
    onSignOut: () => void;
};

/**
 * What both full-screen onboarding pages show about the session: the
 * organisation, and a Sign out that works like the sidebar's (revoke
 * best-effort, then leave for `/login` once the mutation settles, by which
 * time the session is already cleared).
 */
export const useOnboardingSession = (): UseOnboardingSessionResult => {
    const navigate = useNavigate();
    const logoutMutation = useLogoutMutation();
    const organisation = useOrganizationQuery().data;

    const onSignOut = React.useCallback(() => {
        logoutMutation.mutate(undefined, {
            onSettled: () => navigate(PATH.LOGIN, { replace: true }),
        });
    }, [logoutMutation, navigate]);

    return {
        organisation,
        organisationName: organisation?.name.trim() || ONBOARDING_FALLBACK,
        isSigningOut: logoutMutation.isPending,
        onSignOut,
    };
};
